/**
 * Onboarding screen (US-031.3 + multi-persona, 2026-06-16).
 *
 * Two-step first-login flow:
 *   Step 0 — "How will you use NilBoard?" picker. Two options:
 *       (a) Athlete   → continues to Step 1 (NCAA ID + class year).
 *       (b) Staff     → compliance / school / collective / agency / brand.
 *                       Sets `userType=staff` + skips NCAA, drops the
 *                       user on /settings/organizations to create / join
 *                       an org. NCAA flows are suppressed everywhere via
 *                       `user.isOrgStaff` + `userType !== 'athlete'`.
 *
 *   Step 1 (athlete only) — NCAA ID + (optional) class year. Skippable.
 *
 * The redirect runs in `beforeLoad` so a user who has already onboarded
 * never sees a flash of this screen on a deep link to /onboarding.
 */

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from '@app/ui';
import { PageTransition } from '@app/ui/motion';
import { ncaaIdSchema, normalizeNcaaId } from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { NcaaIdHelpPanel } from '../../components/ncaa-id-help-panel';
import { api } from '../../lib/api';

const searchSchema = z.object({
  returnUrl: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/onboarding')({
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ context, search }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    const user = await context.queryClient.ensureQueryData(serverApi.user.me.queryOptions());
    if (user.ncaaId || user.onboardingNcaaIdSkippedAt || user.isOrgStaff) {
      throw redirect({ to: search.returnUrl ?? '/dashboard' });
    }
  },
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(serverApi.user.me.queryOptions());
  },
  component: Onboarding,
});

type Step = 'pick-type' | 'athlete-details';

function Onboarding() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useSuspenseQuery(api.user.me.queryOptions());

  const [step, setStep] = useState<Step>('pick-type');
  const [ncaaId, setNcaaId] = useState('');
  const [classYear, setClassYear] = useState(user.classYear ?? '');
  const [showHelp, setShowHelp] = useState(false);

  const returnTo = search.returnUrl ?? '/dashboard';

  const updateMutation = useMutation({
    ...api.user.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: api.user.me.queryKey() });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  });

  const pickAthlete = () => {
    updateMutation.mutate({ userType: 'athlete' });
    setStep('athlete-details');
  };

  const pickStaff = () => {
    // Staff users go straight to org creation. `isOrgStaff` will flip
    // true the moment they create or get added to an org; until then,
    // `userType=staff` + `onboardingNcaaIdSkippedAt` keep the athlete
    // flows out of the way.
    updateMutation.mutate(
      {
        userType: 'staff',
        onboardingNcaaIdSkippedAt: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: api.user.me.queryKey() });
          toast.success("You're set — create or join an org next.");
          navigate({ to: '/settings/organizations' });
        },
      },
    );
  };

  const pickAgent = () => {
    // Agents also skip the NCAA gate (it's athlete-only), but get their
    // own userType so nav routes them to /agency instead of the
    // compliance surfaces staff see. Their next step is creating the
    // agency org that becomes their roster.
    updateMutation.mutate(
      {
        userType: 'agent',
        onboardingNcaaIdSkippedAt: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: api.user.me.queryKey() });
          toast.success("You're set — create your agency next.");
          navigate({ to: '/settings/organizations' });
        },
      },
    );
  };

  const handleSaveAthlete = () => {
    const normalized = normalizeNcaaId(ncaaId);
    if (!normalized) {
      toast.error('Enter your 10-digit NCAA ID, or skip for now.');
      return;
    }
    if (!ncaaIdSchema.safeParse(normalized).success) {
      toast.error('NCAA ID is 10 digits');
      return;
    }
    updateMutation.mutate(
      { userType: 'athlete', ncaaId: normalized, classYear: classYear || null },
      { onSuccess: () => navigate({ to: returnTo }) },
    );
  };

  const handleSkipAthlete = () => {
    updateMutation.mutate(
      { userType: 'athlete', onboardingNcaaIdSkippedAt: new Date().toISOString() },
      { onSuccess: () => navigate({ to: returnTo }) },
    );
  };

  return (
    <PageTransition>
      <div className="max-w-2xl mx-auto py-12">
        {step === 'pick-type' ? (
          <Card>
            <CardHeader>
              <CardTitle>Welcome to NilBoard</CardTitle>
              <CardDescription>
                How will you use NilBoard? You can change this later in your profile.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <PersonaTile
                title="I'm an athlete"
                subtitle="Track my NIL deals, generate NIL Go submissions, hit reporting thresholds."
                onClick={pickAthlete}
                disabled={updateMutation.isPending}
              />
              <PersonaTile
                title="I'm an agent"
                subtitle="Manage a roster of athletes, track their deals and commissions, recruit new clients."
                onClick={pickAgent}
                disabled={updateMutation.isPending}
              />
              <PersonaTile
                title="I work in compliance / a school, collective, or brand"
                subtitle="Create an organization, invite athletes + staff, run the compliance dashboards."
                onClick={pickStaff}
                disabled={updateMutation.isPending}
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>One quick thing</CardTitle>
              <CardDescription>
                Add your NCAA Eligibility ID so we can link your deals to your school for NIL Go.
                Both fields are optional — skip and add later in your profile.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="ncaaId">NCAA Eligibility ID</Label>
                <Input
                  id="ncaaId"
                  inputMode="numeric"
                  maxLength={14}
                  placeholder="10-digit NCAA ID"
                  value={ncaaId}
                  onChange={(e) => setNcaaId(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowHelp((v) => !v)}
                  className="text-xs text-muted-foreground underline mt-2"
                  aria-expanded={showHelp}
                >
                  {showHelp ? 'Hide help' : "Don't know your NCAA ID?"}
                </button>
                {showHelp ? (
                  <div className="mt-2">
                    <NcaaIdHelpPanel onClose={() => setShowHelp(false)} />
                  </div>
                ) : null}
                <p className="text-xs text-muted-foreground mt-2">
                  Used to link your deals to your school for NIL Go submission.
                </p>
              </div>

              <div>
                <Label htmlFor="classYear">Class year (optional)</Label>
                <select
                  id="classYear"
                  value={classYear ?? ''}
                  onChange={(e) => setClassYear(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select…</option>
                  <option value="freshman">Freshman</option>
                  <option value="sophomore">Sophomore</option>
                  <option value="junior">Junior</option>
                  <option value="senior">Senior</option>
                  <option value="graduate">Graduate</option>
                  <option value="redshirt">Redshirt</option>
                </select>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('pick-type')}
                  disabled={updateMutation.isPending}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  ← Back
                </button>
                <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSkipAthlete}
                    disabled={updateMutation.isPending}
                    className="text-sm text-muted-foreground hover:text-foreground underline"
                  >
                    I'll add this later
                  </button>
                  <Button
                    type="button"
                    onClick={handleSaveAthlete}
                    disabled={updateMutation.isPending}
                    className="min-w-[120px]"
                  >
                    {updateMutation.isPending ? 'Saving…' : 'Save and continue'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageTransition>
  );
}

function PersonaTile({
  title,
  subtitle,
  onClick,
  disabled,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left rounded-lg border-2 border-border hover:border-primary/60 hover:bg-muted/30 transition-colors p-4 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="font-medium">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
    </button>
  );
}
